import { useState, useMemo, useCallback, type ReactNode } from 'react';
import {
  Card,
  Typography,
  Button,
  theme,
  Input,
  Tooltip,
} from 'antd';
import {
  CreditCardOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  RollbackOutlined,
  DollarOutlined,
  ReloadOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { createModuleRouteGuard } from '@/modules/routeGuards';
import PageTransition from '@/components/ui/PageTransition';
import { AutoRefreshToggle } from '@/components/ui/AutoRefreshToggle';
import { PageHeader } from '@/components/ui/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
import { StatCardGrid } from '@/components/ui/StatCardGrid';
import { FilterBar } from '@/components/ui/FilterBar';
import { PaymentsTable } from '../components/PaymentsTable';
import { CreatePaymentLinkModal } from '../components/CreatePaymentLinkModal';
import { usePaymentModulePaymentsQuery } from '../api';
import { AUTOREFRESH_INTERVAL } from '@/constants/autorefresh';
import { useLastUpdatedText } from '@/hooks/useLastUpdatedText';
import { formatCurrency } from '@/utils/format';

const { Text } = Typography;
const { useToken } = theme;

// ---------------------------------------------------------------------------
// Route definition
// ---------------------------------------------------------------------------

/**
 * File-based route definition using TanStack Router virtual file routes.
 * Note: The path string must be a literal (build-time requirement).
 */
export const Route = createFileRoute('/manage/modules/paymentModule/')({
  component: PaymentModulePage,
  beforeLoad: createModuleRouteGuard('payment-module'),
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type StatusFilter = 'all' | 'pending' | 'completed' | 'failed' | 'refunded';

/** Configuration for a single statistics card. */
interface StatCardConfig {
  filter: StatusFilter;
  title: string;
  count: number;
  icon: ReactNode;
  color: string;
  subtitle: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Formats a numeric amount as a rounded currency string (e.g. "$1,234").
 * Uses zero fraction digits for the dashboard summary cards.
 */
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Top-level page for the CMS4 Payments Module.
 *
 * Layout (top to bottom):
 * 1. **Header** — module title, "Create Payment Link" button, auto-refresh toggle.
 * 2. **Statistics row** — four clickable cards that double as status filters
 *    (Total, Completed, Pending, Refunded).
 * 3. **Toolbar** — search input, active-filter indicator, result count, manual refresh.
 * 4. **PaymentsTable** — the full table with inline modals for details / gateway / refund.
 *
 * Data is polled automatically via `usePaymentModulePaymentsQuery`
 * and can be toggled on/off with the `AutoRefreshToggle`.
 */
export function PaymentModulePage() {
  const { token } = useToken();
  const { t } = useTranslation('payment-module');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  /** Payments query with configurable auto-refresh interval. */
  const {
    data: paymentsData,
    isLoading,
    error,
    refetch,
    isFetching,
    dataUpdatedAt,
  } = usePaymentModulePaymentsQuery(
    autoRefresh ? AUTOREFRESH_INTERVAL : false,
  );

  // -------------------------------------------------------------------------
  // Derived / memoised values
  // -------------------------------------------------------------------------
  const lastUpdatedText = useLastUpdatedText(dataUpdatedAt);

  /**
   * Aggregate statistics computed from the full (unfiltered) payments array.
   * Used by the dashboard cards and the results counter.
   */
  const stats = useMemo(() => {
    const payments = paymentsData?.data ?? [];

    const completed = payments.filter((p) => p.status.toLowerCase() === 'completed');
    const pending = payments.filter((p) => p.status.toLowerCase() === 'pending');
    const failed = payments.filter((p) => p.status.toLowerCase() === 'failed');
    const refunded = payments.filter((p) => p.status.toLowerCase() === 'refunded');

    const totalAmount = payments.reduce((sum, p) => sum + p.amount, 0);
    const completedAmount = completed.reduce((sum, p) => sum + p.amount, 0);
    const pendingAmount = pending.reduce((sum, p) => sum + p.amount, 0);
    const refundedAmount = refunded.reduce((sum, p) => sum + (p.refundedAmount || 0), 0);

    return {
      total: payments.length,
      completed: completed.length,
      pending: pending.length,
      failed: failed.length,
      refunded: refunded.length,
      totalAmount,
      completedAmount,
      pendingAmount,
      refundedAmount,
      netRevenue: completedAmount - refundedAmount,
      currency: payments[0]?.currency || 'EUR',
    };
  }, [paymentsData]);

  /**
   * Payments filtered by the active status tab and the free-text search query.
   * Search matches against customer name or email (case-insensitive).
   */
  const filteredPayments = useMemo(() => {
    let payments = paymentsData?.data ?? [];

    if (statusFilter !== 'all') {
      payments = payments.filter((p) => p.status.toLowerCase() === statusFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      payments = payments.filter(
        (p) =>
          p.customerName.toLowerCase().includes(query) ||
          p.customerEmail.toLowerCase().includes(query),
      );
    }

    return payments;
  }, [paymentsData, statusFilter, searchQuery]);

  /** Whether any filter (status or search) is active. */
  const hasActiveFilters = statusFilter !== 'all' || searchQuery.trim() !== '';

  /** Configuration for the four dashboard statistic cards. */
  const statCards: StatCardConfig[] = useMemo(
    () => [
      {
        filter: 'all',
        title: t('stats.totalPayments'),
        count: stats.total,
        icon: <DollarOutlined />,
        color: token.colorPrimary,
        subtitle: t('stats.totalValue', { amount: formatCurrency(stats.totalAmount, stats.currency, 0) }),
      },
      {
        filter: 'completed',
        title: t('stats.completed'),
        count: stats.completed,
        icon: <CheckCircleOutlined />,
        color: token.colorSuccess,
        subtitle: t('stats.revenue', { amount: formatCurrency(stats.completedAmount, stats.currency, 0) }),
      },
      {
        filter: 'pending',
        title: t('stats.pending'),
        count: stats.pending,
        icon: <ClockCircleOutlined />,
        color: token.colorWarning,
        subtitle: t('stats.awaiting', { amount: formatCurrency(stats.pendingAmount, stats.currency, 0) }),
      },
      {
        filter: 'refunded',
        title: t('stats.refunded'),
        count: stats.refunded,
        icon: <RollbackOutlined />,
        color: token.colorError,
        subtitle: t('stats.returned', { amount: formatCurrency(stats.refundedAmount, stats.currency, 0) }),
      },
    ],
    [stats, token, t],
  );

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  /** Opens the "Create Payment Link" modal. */
  const handleOpenCreateModal = useCallback(() => {
    setCreateModalOpen(true);
  }, []);

  /** Closes the "Create Payment Link" modal. */
  const handleCloseCreateModal = useCallback(() => {
    setCreateModalOpen(false);
  }, []);

  /** Called after a payment link is successfully created — triggers a data refresh. */
  const handleCreateSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  /** Clears both the status filter and the search query. */
  const handleClearFilters = useCallback(() => {
    setStatusFilter('all');
    setSearchQuery('');
  }, []);

  /** Updates the search query as the user types. */
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    [],
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <PageTransition animation="fadeInUp">
      <PageHeader
        icon={<CreditCardOutlined />}
        title={t('pageTitle')}
        description={t('pageDescription')}
        actions={
          <>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreateModal}>
              {t('createPaymentLink')}
            </Button>
            <AutoRefreshToggle
              autoRefresh={autoRefresh}
              onAutoRefreshChange={setAutoRefresh}
              isFetching={isFetching}
              lastUpdated={lastUpdatedText}
            />
          </>
        }
      />

      {/* Statistics cards — clickable to filter the table by status */}
      <StatCardGrid>
        {statCards.map((card) => (
          <StatCard
            key={card.filter}
            label={card.title}
            value={card.count}
            hint={card.subtitle}
            icon={card.icon}
            color={card.color}
            active={statusFilter === card.filter}
            onClick={() => setStatusFilter(card.filter)}
          />
        ))}
      </StatCardGrid>

      {/* Search, filter reset, result count and manual refresh */}
      <FilterBar
        actions={
          <>
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {hasActiveFilters
                ? t('paymentCountOf', { count: filteredPayments.length, total: stats.total })
                : t('paymentCount', { count: filteredPayments.length })}
            </Text>
            {hasActiveFilters && (
              <Button icon={<CloseOutlined />} onClick={handleClearFilters}>
                {t('clearFilters')}
              </Button>
            )}
            <Tooltip title={t('refresh')}>
              <Button
                icon={<ReloadOutlined spin={isFetching} />}
                onClick={() => refetch()}
                aria-label={t('refresh')}
              />
            </Tooltip>
          </>
        }
      >
        <Input.Search
          placeholder={t('searchPlaceholder')}
          allowClear
          value={searchQuery}
          onChange={handleSearchChange}
          style={{ flex: '1 1 220px', minWidth: 160, maxWidth: 360 }}
        />
      </FilterBar>

      {/* Payments table */}
      <Card
        styles={{ body: { padding: 0 } }}
        style={{
          overflow: 'hidden',
        }}
      >
        <PaymentsTable
          data={filteredPayments}
          isLoading={isLoading}
          error={error}
          onRefresh={refetch}
        />
      </Card>

      {/* Create payment link modal */}
      <CreatePaymentLinkModal
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        onSuccess={handleCreateSuccess}
      />
    </PageTransition>
  );
}
